import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  Canceler,
} from 'axios'
import axios from 'axios'
import FileDownload from 'js-file-download'

import type { PageOptions } from '@/api/definition'

/**
 * Reads the reason out of a failed request.
 *
 * The SAM API reports failures through Go's `http.Error`, which writes the
 * reason as a plain-text body rather than as JSON, so reading `data.message`
 * alone loses the message the backend sent. A JSON `{ message }` body is
 * still honoured. Returns undefined when the response carries no reason, so
 * that the caller can fall back to a translated message of its own.
 */
export function apiErrorMessage(error: unknown): string | undefined {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data
    if (typeof data === 'string' && data.trim().length > 0) {
      return data.trim()
    }
    return (data as { message?: string } | undefined)?.message || undefined
  }
  if (error instanceof Error && error.message) return error.message
  return undefined
}

export function setPaginationParameters({
  pageIndex,
  pageSize,
  sortBy,
  globalFilter,
}: PageOptions) {
  const params = new URLSearchParams()
  if (globalFilter) params.set('search', globalFilter)
  params.set('page', `${pageIndex + 1}`)
  params.set('per_page', `${pageSize}`)
  if (sortBy.length > 0) {
    const [{ id, desc }] = sortBy
    params.set('sort', id)
    params.set('sort_dir', desc ? 'desc' : 'asc')
  }
  return params
}

const useNonConcurrentRequest = function (client: AxiosInstance = axios): {
  request: <T = unknown, R = AxiosResponse<T>, D = unknown>(
    cfg: Omit<AxiosRequestConfig<D>, 'cancelToken'>,
  ) => Promise<R>
} {
  let canceler: Canceler

  return {
    request: async <T = unknown, R = AxiosResponse<T>, D = unknown>(
      cfg: AxiosRequestConfig<D>,
    ): Promise<R> => {
      if (canceler) canceler()
      const { token, cancel } = axios.CancelToken.source()
      canceler = cancel
      cfg.cancelToken = token
      return client.request(cfg)
    },
  }
}

export default useNonConcurrentRequest

export async function downloadFile(
  url: string,
  client: AxiosInstance = axios,
): Promise<void> {
  try {
    const response = await client.get(url, {
      responseType: 'blob',
    })
    FileDownload(
      response.data,
      response.headers['content-disposition'].split('filename=')[1],
      response.headers['content-type'],
    )
  } catch {
    // Caught by interceptor
  }
}
